/**
 * State and transport for the Support Chat console, backed by ant's GraphQL
 * chat model.
 *
 * Live path: two ant subscriptions over one Phoenix websocket (./chat/socket.ts).
 * `chatSessionUpdated` (no arguments: the token's scope decides what arrives)
 * keeps the queue in step as sessions open, are anchored, assigned,
 * prioritised, tagged or closed; `chatMessageSent` (no conversationId, so
 * every session the token may read) appends to the open thread and moves the
 * queue row, chime and unread badge the moment a rider writes. Events are
 * folded through ./chat/live.ts: keyed by id (a resend publishes the same
 * message again) and sorted by insertedAt (order is not guaranteed).
 *
 * Fallback path: while the socket is not live, the queue and the open thread
 * are polled every 3 seconds, exactly as before subscriptions existed. While
 * it is live the same polls run every 30 seconds as a safety net, and every
 * switch between the two ticks at once, which is also how the console catches
 * up on whatever ant published while the socket was down. Polling pauses
 * while the tab is hidden; a tick never overlaps the previous one.
 *
 * `isConnected` ("Live" in the list header) is the socket: open, with both
 * subscriptions acknowledged by ant. `transport` says what is actually
 * feeding the console: the socket, the HTTP polls, or nothing.
 *
 * The pages were written against a socket.io chat service, so this hook keeps
 * that service's vocabulary (a "conversation" here is an ant ChatSession) and
 * return shape. See ./chat/mappers.ts for the translation.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { displayError } from '../utils';
import * as chatApi from './chat/api';
import { AntChatMessage, AntChatSession } from './chat/api';
import { isForbiddenError } from './chat/client';
import {
  foldAssistantSession,
  foldClosedSession,
  foldMessage,
  foldMessageIntoQueue,
  foldSession,
  keepDerivedContext,
  mergeThread,
} from './chat/live';
import {
  fullName,
  isAfter,
  laterOf,
  latestContextHardwareId,
  latestRentalId,
  latestUserLocation,
  sameList,
  mapMessage,
  mapSession,
  riderLabel,
  systemLabel,
  unreadCount,
  withRiderIdentity,
} from './chat/mappers';
import {
  ChatSessionEvent,
  ChatSocketState,
  closeChatSocket,
  getChatSocketState,
  onChatSocketState,
  subscribeMessages,
  subscribeSessions,
} from './chat/socket';
import { compareConversations } from './chat/queueOrder';
import { Conversation, Message } from './chat/types';

export type {
  Conversation,
  ConversationStatus,
  Message,
  MessageAction,
} from './chat/types';

/** How the console is being fed right now. */
export type ChatTransport = 'socket' | 'polling' | 'offline' | 'connecting';

/** Poll cadence while the socket is not live: the console's only feed. */
const POLL_INTERVAL_MS = 3000;
/** Poll cadence while the socket is live: a safety net behind the subscriptions. */
const SAFETY_NET_INTERVAL_MS = 30000;
/**
 * A rider whose name could not be read is asked for again after 60 s, then
 * 2 min, then 5 min, then not again on this page load.
 */
export const RIDER_RETRY_MS = [60000, 120000, 300000];
/**
 * A rider message that lands in the thread the agent has open is marked read
 * this long after it lands; whatever else lands meanwhile rides along.
 */
const OPEN_THREAD_READ_DELAY_MS = 1000;

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/** ant's session revision includes microseconds, which Date.parse alone drops. */
const sessionRevision = (session: AntChatSession): number => {
  const fraction = session.updatedAt.match(/\.(\d+)/)?.[1] ?? '';
  return (
    Date.parse(session.updatedAt) * 1000 + Number(fraction.padEnd(6, '0').slice(3, 6))
  );
};

function newClientMessageId(): string {
  const cryptoApi = window.crypto as Crypto & { randomUUID?: () => string };
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  // RFC 4122 v4 from getRandomValues, for browsers without randomUUID.
  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    // No Web Crypto (old runtimes, the test DOM): still unique enough for a retry key.
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20
  )}-${hex.slice(20)}`;
}

/**
 * Runs `task` now and every `intervalMs` while `enabled`, re-keyed by `key`
 * and by the interval itself (so switching cadence ticks at once). Skips
 * ticks while the tab is hidden (except the first one, or all of them while
 * `whileHidden`) or the previous tick is still running, and runs one as soon
 * as the tab is visible again.
 */
function usePolling(
  enabled: boolean,
  key: string | null,
  intervalMs: number,
  task: () => Promise<void>,
  whileHidden = false
) {
  const taskRef = useRef(task);
  taskRef.current = task;
  const whileHiddenRef = useRef(whileHidden);
  whileHiddenRef.current = whileHidden;

  useEffect(() => {
    if (!enabled) return undefined;

    let disposed = false;
    let inFlight = false;

    const tick = async (evenIfHidden = false) => {
      if (disposed || inFlight) return;
      if (!evenIfHidden && document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        await taskRef.current();
      } finally {
        inFlight = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tick();
    };

    // The first read runs even in a background tab, so the page is not
    // empty when the agent switches to it; after that, hidden tabs rest.
    tick(true);
    const interval = window.setInterval(() => tick(whileHiddenRef.current), intervalMs);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      disposed = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, key, intervalMs]);
}

export interface UseChatOptions {
  /** Opens a conversation on screen: what a desktop notification's click asks for. */
  onOpen?: (conv: Conversation) => void;
}

export function useChat(
  adminEmail: string,
  adminName: string,
  { onOpen }: UseChatOptions = {}
) {
  /** The page's latest opener, read when a notification is clicked, whenever that is. */
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const [socketState, setSocketState] = useState<ChatSocketState>('closed');
  const [sessionsLive, setSessionsLive] = useState(false);
  const [messagesLive, setMessagesLive] = useState(false);
  /** Whether the last HTTP poll succeeded, for `transport` while the socket is down. */
  const [httpOk, setHttpOk] = useState(false);
  /** The queue has been read from ant at least once (for the loading state). */
  const [queueLoaded, setQueueLoaded] = useState(false);
  /** A poll has answered, well or not: before that the console is connecting. */
  const [pollAnswered, setPollAnswered] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  /** Closed sessions: browsed through the Closed filter, or closed while watched. */
  const [closedConversations, setClosedConversations] = useState<Conversation[]>([]);
  const [closedLoading, setClosedLoading] = useState(false);
  /** Sessions the assistant is answering: shown read-only under the Assistant filter. */
  const [assistantConversations, setAssistantConversations] = useState<Conversation[]>(
    []
  );
  const [assistantLoaded, setAssistantLoaded] = useState(false);
  /** The Assistant filter is on screen: its list is polled like the queue. */
  const [assistantWatched, setAssistantWatched] = useState(false);
  const [messages, setMessages] = useState<Record<string, Message[]>>({});
  const [localReadAt, setLocalReadAt] = useState<Record<string, string>>({});
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const activeConvIdRef = useRef<string | null>(null);
  /**
   * What the socket delivered for the open thread since its last read. Before
   * that thread's first read answers there is nothing to fold a message into,
   * so without this it would wait for the next poll; the read merges it.
   */
  const liveSinceReadRef = useRef<Record<string, Message[]>>({});

  const isConnected = socketState === 'open' && sessionsLive && messagesLive;
  const transport: ChatTransport = isConnected
    ? 'socket'
    : httpOk
    ? 'polling'
    : pollAnswered
    ? 'offline'
    : 'connecting';

  // Refs so polling ticks and socket events read current state without stale closures.
  const conversationsRef = useRef<Conversation[]>([]);
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);
  const closedRef = useRef<Conversation[]>([]);
  useEffect(() => {
    closedRef.current = closedConversations;
  }, [closedConversations]);
  const assistantRef = useRef<Conversation[]>([]);
  useEffect(() => {
    assistantRef.current = assistantConversations;
  }, [assistantConversations]);
  /** The assistant is answering this session: no chime, no read mark from Support. */
  const isAssistantSession = useCallback(
    (convId: string): boolean => assistantRef.current.some((c) => c.id === convId),
    []
  );
  const localReadAtRef = useRef<Record<string, string>>({});
  useEffect(() => {
    localReadAtRef.current = localReadAt;
  }, [localReadAt]);

  /** ant conversation id by session id, for sessions no longer in the queue. */
  const conversationIdsRef = useRef<Record<string, string>>({});
  /**
   * A session openSession just fetched, for the thread opened with it: it is
   * folded into its list, but the list refs only catch up once that render
   * commits. The next resolveConversation for it takes it.
   */
  const justOpenedRef = useRef<Record<string, Conversation>>({});
  /** Newest rider message seen per session, so a message chimes once (poll or event). */
  const lastSeenRef = useRef<Record<string, string>>({});
  const firstQueuePollDoneRef = useRef(false);
  /** Sessions being fetched because a message named one the queue did not know. */
  const adoptingRef = useRef<Map<string, Promise<AntChatSession | null>>>(new Map());
  /**
   * When the socket last touched each session (ms). A poll answering after an
   * event does not overwrite that row: the event is newer than the poll's read.
   */
  const touchedAtRef = useRef<Record<string, number>>({});
  /** Server snapshots, apart from message activity and the browser's clock. */
  const sessionSnapshotsRef = useRef<Record<string, AntChatSession>>({});
  const rememberSession = useCallback((session: AntChatSession): AntChatSession => {
    const previous = sessionSnapshotsRef.current[session.id];
    if (previous && sessionRevision(previous) > sessionRevision(session)) return previous;
    sessionSnapshotsRef.current[session.id] = session;
    return session;
  }, []);
  /** An uncertain send keeps its key until success or an edited payload replaces it. */
  const pendingSendsRef = useRef(
    new Map<string, { payload: string; clientMessageId: string }>()
  );

  /**
   * The rider messages each thread held when it was first fetched, and every
   * one that reached it since. A rider message missing from here is one that
   * landed while the console watched, from the socket or a poll; the first
   * fetch is not news (the page reads that on open).
   */
  const threadRiderIdsRef = useRef<Record<string, Set<string>>>({});
  /** markAsRead calls per session: a pending read one of them covered is dropped. */
  const readRequestsRef = useRef<Record<string, number>>({});
  /** The open thread's read, waiting out OPEN_THREAD_READ_DELAY_MS. */
  const pendingReadRef = useRef<{
    convId: string;
    timer: number;
    requests: number;
  } | null>(null);
  /** markAsRead, for the pending read (it is defined further down). */
  const markAsReadRef = useRef<(convId: string) => Promise<void>>(async () => {});

  /**
   * The rider's name and phone per ant conversation id, read apart from the
   * queue (chatApi.fetchRiderIdentities): a token without user:read still
   * gets the queue, only without names.
   */
  const [riders, setRiders] = useState<Record<string, chatApi.RiderIdentity>>({});
  const ridersRef = useRef(riders);
  ridersRef.current = riders;
  const riderLookupRef = useRef<{
    pending: Set<string>;
    failedAt: Record<string, number>;
    failures: Record<string, number>;
    /** The last read of the open sessions' riders: the others wait for it. */
    openChain: Promise<void>;
  }>({
    pending: new Set(),
    failedAt: {},
    failures: {},
    openChain: Promise.resolve(),
  });
  // Bumped when a rider could not be read and when a failed rider's wait is
  // over, so the lookup runs again.
  const [riderRetryTick, setRiderRetryTick] = useState(0);

  /** The signed-in admin's ant id, for "Assign to me" and "mine". */
  const [myAdminId, setMyAdminId] = useState<string | null>(null);
  useEffect(() => {
    if (!adminEmail) return undefined;
    let cancelled = false;
    chatApi
      .fetchAdminByEmail(adminEmail)
      .then((me) => {
        if (!cancelled) setMyAdminId(me?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setMyAdminId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [adminEmail]);

  // Desktop notification permission is asked from a banner on the page
  // (NotificationBanner): browsers want it asked from a click.

  /**
   * Rider messages that arrived while the tab was hidden, for the tab's
   * title; back to 0 when the agent returns to the tab.
   */
  const [arrivedWhileHidden, setArrivedWhileHidden] = useState(0);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') setArrivedWhileHidden(0);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // One audio context per mounted console; each chime releases its own nodes.
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioMountedRef = useRef(true);
  useEffect(() => {
    audioMountedRef.current = true;
    return () => {
      audioMountedRef.current = false;
      const context = audioContextRef.current;
      audioContextRef.current = null;
      if (context && context.state !== 'closed') context.close().catch(() => {});
    };
  }, []);

  const playChime = useCallback(() => {
    // A late fetch of an unknown session can finish after the console leaves.
    if (!audioMountedRef.current) return;
    try {
      const ctx: AudioContext =
        audioContextRef.current ??
        new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = ctx;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.onended = () => {
        osc.disconnect();
        gain.disconnect();
      };
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1047, ctx.currentTime);
      osc.frequency.setValueAtTime(1319, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.22, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
      osc.start();
      osc.stop(ctx.currentTime + 0.45);
    } catch (_) {}
  }, []);

  /**
   * A rider message nobody has seen: chime, and in a hidden tab count it in
   * the title and raise a desktop notification. `chime: false` for all but
   * the first of a batch, so one poll that finds several does not play over itself.
   */
  const notifyNewRiderMessage = useCallback(
    (conv: Conversation | undefined, { chime = true }: { chime?: boolean } = {}) => {
      if (chime) playChime();
      if (document.visibilityState === 'visible') return;
      setArrivedWhileHidden((n) => n + 1);
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const named =
          conv &&
          withRiderIdentity(
            conv,
            conv.conversation_id ? ridersRef.current[conv.conversation_id] : undefined
          );
        try {
          const notification = new Notification(
            named ? riderLabel(named) : 'Support Chat',
            {
              body: named?.system_name
                ? `New message from a rider · ${systemLabel(named)}`
                : 'New message from a rider',
              icon: '/favicon.ico',
              // One notification per conversation: a burst replaces, not stacks.
              tag: conv ? `support-chat-${conv.id}` : 'support-chat',
            }
          );
          notification.onclick = () => {
            window.focus();
            // The chat the rider wrote in, not only the tab.
            if (conv) onOpenRef.current?.(conv);
            notification.close();
          };
        } catch (_) {
          // Some browsers only allow notifications from a service worker.
        }
      }
    },
    [playChime]
  );

  /** The agent is looking at this session right now: it is open on screen, in a visible tab. */
  const isWatched = useCallback(
    (convId: string): boolean =>
      activeConvIdRef.current === convId && document.visibilityState === 'visible',
    []
  );

  /** Closed as far as the console knows (markAsRead asks ant when it does not). */
  const isKnownClosed = useCallback(
    (convId: string): boolean =>
      closedRef.current.some((c) => c.id === convId) ||
      conversationsRef.current.some((c) => c.id === convId && c.status === 'closed'),
    []
  );

  /**
   * Marks the open thread read OPEN_THREAD_READ_DELAY_MS from now, so the
   * rider sees "Seen" while the agent is on it. Rider messages landing
   * meanwhile ride along: one read per burst, not one per message. Dropped
   * when, by then, the agent has moved on or hidden the tab, the session has
   * closed, or a read requested since (the page's, on open) already covers it.
   */
  const readOpenThreadSoon = useCallback(
    (convId: string) => {
      if (!isWatched(convId) || isKnownClosed(convId)) return;
      // Support only watches an assistant session: the rider's "Seen" is not ours to give.
      if (isAssistantSession(convId)) return;
      const requests = readRequestsRef.current[convId] ?? 0;
      const pending = pendingReadRef.current;
      if (pending?.convId === convId) {
        // It must cover this message too: a read requested before it landed no longer does.
        pending.requests = requests;
        return;
      }
      if (pending) window.clearTimeout(pending.timer);
      const timer = window.setTimeout(() => {
        pendingReadRef.current = null;
        if (!isWatched(convId) || isKnownClosed(convId)) return;
        if ((readRequestsRef.current[convId] ?? 0) !== requests) return;
        markAsReadRef.current(convId);
      }, OPEN_THREAD_READ_DELAY_MS);
      pendingReadRef.current = { convId, timer, requests };
    },
    [isAssistantSession, isKnownClosed, isWatched]
  );

  // A read still waiting when the page goes away goes with it.
  useEffect(
    () => () => {
      if (pendingReadRef.current) window.clearTimeout(pendingReadRef.current.timer);
      pendingReadRef.current = null;
    },
    []
  );

  /**
   * Rider messages that reached a thread, from the socket or a fetch. A
   * thread's first fetch only records what it holds; after that, a rider
   * message it did not hold has just landed, and is read if the agent is
   * watching. Keyed by id, so each one asks at most once and a read, which
   * lands no message, can never ask for another.
   */
  const noteRiderMessages = useCallback(
    (convId: string, ids: string[], fromFetch: boolean) => {
      const known = threadRiderIdsRef.current[convId];
      if (!known) {
        if (fromFetch) threadRiderIdsRef.current[convId] = new Set(ids);
        return;
      }
      const landed = ids.filter((id) => !known.has(id));
      if (landed.length === 0) return;
      landed.forEach((id) => known.add(id));
      readOpenThreadSoon(convId);
    },
    [readOpenThreadSoon]
  );

  /** Stores a thread and folds what it says about the rider into its session. */
  const applyMessages = useCallback(
    (convId: string, rows: AntChatMessage[], userName: string | undefined): Message[] => {
      const sessionRows = rows.filter((m) => m.sessionId === convId);
      const mapped = sessionRows.map((m) =>
        mapMessage(m, { sessionId: convId, userName })
      );
      // Union with what the socket delivered meanwhile, including what came
      // before the thread had loaded at all; same rows as last tick keep the
      // previous reference so nothing repaints.
      const liveSinceRead = liveSinceReadRef.current[convId] ?? [];
      delete liveSinceReadRef.current[convId];
      setMessages((prev) => {
        const existing = prev[convId];
        const onScreen = liveSinceRead.reduce(foldMessage, existing ?? []);
        const merged = onScreen.length ? mergeThread(onScreen, mapped) : mapped;
        return sameList(existing, merged) ? prev : { ...prev, [convId]: merged };
      });
      noteRiderMessages(
        convId,
        mapped.filter((m) => m.sender_type === 'user').map((m) => m.id),
        true
      );

      const location = latestUserLocation(sessionRows);
      const hardwareId = latestContextHardwareId(sessionRows);
      const rentalId = latestRentalId(sessionRows);
      if (location || hardwareId || rentalId) {
        setConversations((prev) => {
          const next = prev.map((c) =>
            c.id === convId
              ? {
                  ...c,
                  user_lat: location?.lat ?? c.user_lat,
                  user_lng: location?.lng ?? c.user_lng,
                  context_hardware_id: hardwareId ?? c.context_hardware_id,
                  // The session's own rental wins; a general session shows
                  // the rental the rider last wrote about.
                  context_rental_id: c.context_rental_id ?? rentalId,
                }
              : c
          );
          return sameList(prev, next) ? prev : next;
        });
      }
      return mapped;
    },
    [noteRiderMessages]
  );

  /** The session as the console knows it, fetched from ant when not in the queue. */
  const resolveConversation = useCallback(
    async (convId: string): Promise<Conversation> => {
      const justOpened = justOpenedRef.current[convId];
      delete justOpenedRef.current[convId];
      const known =
        conversationsRef.current.find((c) => c.id === convId) ??
        closedRef.current.find((c) => c.id === convId) ??
        justOpened;
      if (known?.conversation_id) return known;
      const session = await chatApi.fetchSession(convId);
      if (!session) throw new Error('This conversation no longer exists');
      const mapped = mapSession(rememberSession(session));
      conversationIdsRef.current[convId] = mapped.conversation_id ?? '';
      return known ? { ...known, conversation_id: mapped.conversation_id } : mapped;
    },
    [rememberSession]
  );

  const resolveConversationId = useCallback(
    async (convId: string): Promise<string> => {
      const cached = conversationIdsRef.current[convId];
      if (cached) return cached;
      const conv = await resolveConversation(convId);
      if (!conv.conversation_id)
        throw new Error('This conversation has no ant conversation');
      return conv.conversation_id;
    },
    [resolveConversation]
  );

  /**
   * Marks the rider's conversation read by Customer Support. ant keeps one
   * read mark per rider conversation, not per session, so reading a CLOSED
   * session (the Closed filter, "Earlier conversations") must not touch it:
   * it would clear the rider's current open session too. Closed sessions are
   * skipped here, whoever calls.
   */
  const markAsRead = useCallback(
    async (convId: string) => {
      readRequestsRef.current[convId] = (readRequestsRef.current[convId] ?? 0) + 1;
      if (closedRef.current.some((c) => c.id === convId)) return;
      try {
        const conv = await resolveConversation(convId);
        if (conv.status === 'closed') return;
        const conversationId = await resolveConversationId(convId);
        if (!isWatched(convId) || isAssistantSession(convId)) return;
        const readAt = new Date().toISOString();
        const conversation = await chatApi.markChatReadByAdmin(conversationId);
        // Only confirmed acknowledgements suppress unread/retries. Keep the
        // request's start time, and never regress a later successful read.
        setLocalReadAt((prev) => ({
          ...prev,
          [convId]: laterOf(prev[convId], readAt) ?? readAt,
        }));
        setConversations((prev) =>
          prev.map((c) =>
            c.id === convId
              ? {
                  ...c,
                  last_read_at_admin: laterOf(
                    conversation.lastReadAtAdmin,
                    c.last_read_at_admin
                  ),
                }
              : c
          )
        );
      } catch (error) {
        // An admin who may read the chat but not write to it (the Support
        // Viewer role lacks chat-conversation:update): their reading is not
        // Support's "seen", and ant saying so is no error to show.
        if (isForbiddenError(error)) return;
        displayError(`Could not mark the conversation as read. ${errorMessage(error)}`);
      }
    },
    [isAssistantSession, isWatched, resolveConversation, resolveConversationId]
  );
  markAsReadRef.current = markAsRead;

  // ── Live: a session opened or changed ──────────────────────────────────────
  const applySession = useCallback(
    (snapshot: AntChatSession): AntChatSession => {
      const session = rememberSession(snapshot);
      // A delayed mutation or fetch must not undo a newer assignment or closure.
      if (session !== snapshot) return session;
      conversationIdsRef.current[session.id] = session.conversationId ?? '';
      touchedAtRef.current[session.id] = Date.now();
      setConversations((prev) => foldSession(prev, session));
      // The assistant's list: in while it answers, out once a person has it.
      setAssistantConversations((prev) => foldAssistantSession(prev, session));
      // Closed (by this agent, another one, or the rider): it leaves the queue
      // for the closed list, where the page keeps showing it read-only.
      if ((session.status ?? '').toLowerCase() === 'closed') {
        setClosedConversations((prev) => foldClosedSession(prev, session));
      }
      return session;
    },
    [rememberSession]
  );

  const onSessionEvent = useCallback(
    (event: ChatSessionEvent) => {
      const session = event.created ?? event.updated;
      if (session) applySession(session);
    },
    [applySession]
  );

  /**
   * A message named a session the queue does not hold yet (its `created`
   * event may still be on its way, or was published before this page opened):
   * fetch the session once and fold it in.
   */
  const adoptSession = useCallback(
    (sessionId: string): Promise<AntChatSession | null> => {
      // A burst of messages for one session shares one fetch.
      const pending = adoptingRef.current.get(sessionId);
      if (pending) return pending;
      const request = (async () => {
        try {
          const session = await chatApi.fetchSession(sessionId);
          return session ? applySession(session) : null;
        } catch (error) {
          // The next poll of the queue catches up.
          return null;
        } finally {
          adoptingRef.current.delete(sessionId);
        }
      })();
      adoptingRef.current.set(sessionId, request);
      return request;
    },
    [applySession]
  );

  /**
   * A conversation opened by its link (a Slack alert, Copy link): fetched
   * from ant, folded into the list it belongs to (queue, assistant, closed)
   * and returned as the console shows it. ant answers null for a session
   * this token may not read and for one that does not exist alike.
   */
  const openSession = useCallback(
    async (sessionId: string): Promise<Conversation> => {
      const session = await chatApi.fetchSession(sessionId);
      if (!session) {
        throw new Error(
          'This conversation is not available to you, or no longer exists.'
        );
      }
      const opened = mapSession(applySession(session));
      justOpenedRef.current[opened.id] = opened;
      return opened;
    },
    [applySession]
  );

  /**
   * The session as ant has it now, folded into its list and returned as the
   * console shows it; null when ant does not return it. For a write that
   * must not trust the list, which can be behind ant (an event on its way,
   * or the time between polls while the socket is down).
   */
  const refreshSession = useCallback(
    async (sessionId: string): Promise<Conversation | null> => {
      const session = await chatApi.fetchSession(sessionId);
      if (!session) return null;
      return mapSession(applySession(session));
    },
    [applySession]
  );

  // ── Live: a message was written ────────────────────────────────────────────
  const onMessageCreated = useCallback(
    (row: AntChatMessage) => {
      const sessionId = row.sessionId;
      const known = conversationsRef.current.find((c) => c.id === sessionId);
      const assistantKnown = assistantRef.current.find((c) => c.id === sessionId);
      const message = mapMessage(row, {
        sessionId,
        userName: (known ?? assistantKnown)?.user_name,
      });

      // The open thread keeps it for its next read too, which may be its first.
      if (sessionId === activeConvIdRef.current) {
        const live = liveSinceReadRef.current;
        live[sessionId] = foldMessage(live[sessionId] ?? [], message);
      }

      // The thread, when the agent has loaded it; a resend is dropped by id.
      setMessages((prev) => {
        const thread = prev[sessionId];
        if (!thread) return prev;
        const next = foldMessage(thread, message);
        return next === thread ? prev : { ...prev, [sessionId]: next };
      });

      const riderMessageLanded = (conv: Conversation | undefined) => {
        if (message.sender_type !== 'user') return;
        // A rider message not seen before chimes once, whichever of this
        // event and the queue poll saw it first.
        const seen = lastSeenRef.current[sessionId];
        if (!seen || isAfter(message.created_on, seen)) {
          lastSeenRef.current[sessionId] = message.created_on;
          notifyNewRiderMessage(conv);
        }
        // Landed in the thread the agent has open in a visible tab: it is
        // read. Not tied to the chime: the queue poll may have counted it first.
        noteRiderMessages(sessionId, [message.id], false);
      };

      // The queue row: when the rider last wrote, from where, about what.
      if (known) {
        touchedAtRef.current[sessionId] = Date.now();
        setConversations((prev) => foldMessageIntoQueue(prev, row, message));
        riderMessageLanded(known);
      } else if (assistantKnown) {
        // The assistant is answering it: its row moves, nobody is called.
        setAssistantConversations((prev) => foldMessageIntoQueue(prev, row, message));
      } else {
        // Unknown until ant says whose it is: the assistant's sessions call nobody.
        adoptSession(sessionId).then((session) => {
          const adopted = session ? mapSession(session) : undefined;
          if (adopted?.handled_by === 'assistant') return;
          riderMessageLanded(
            conversationsRef.current.find((c) => c.id === sessionId) ?? adopted
          );
        });
      }
    },
    [adoptSession, noteRiderMessages, notifyNewRiderMessage]
  );

  // ── The socket: one per page, both subscriptions for as long as it is open ─
  const liveHandlersRef = useRef({ onSessionEvent, onMessageCreated });
  liveHandlersRef.current = { onSessionEvent, onMessageCreated };

  useEffect(() => {
    if (!adminEmail) return undefined;
    setSocketState(getChatSocketState());
    const offState = onChatSocketState(setSocketState);
    const offSessions = subscribeSessions(
      (event) => liveHandlersRef.current.onSessionEvent(event),
      setSessionsLive
    );
    const offMessages = subscribeMessages(
      null,
      (row) => liveHandlersRef.current.onMessageCreated(row),
      setMessagesLive
    );
    return () => {
      offSessions();
      offMessages();
      offState();
      closeChatSocket();
      setSessionsLive(false);
      setMessagesLive(false);
      setSocketState('closed');
    };
  }, [adminEmail]);

  // ── Polling: the queue ─────────────────────────────────────────────────────
  const pollQueue = useCallback(async () => {
    const startedAt = Date.now();
    let sessions: chatApi.AntChatSession[];
    try {
      sessions = await chatApi.fetchQueue();
    } catch (error) {
      setHttpOk(false);
      setPollAnswered(true);
      return;
    }
    setHttpOk(true);
    setPollAnswered(true);
    setQueueLoaded(true);
    /** The socket touched this session after the poll's read: its row is newer. */
    const touched = (id: string) => (touchedAtRef.current[id] ?? 0) >= startedAt;

    const mapped = sessions
      .map((session) =>
        touched(session.id)
          ? sessionSnapshotsRef.current[session.id] ?? session
          : rememberSession(session)
      )
      .map(mapSession)
      .filter((c) => c.status !== 'closed' && c.handled_by !== 'assistant');
    mapped.forEach((c) => {
      conversationIdsRef.current[c.id] = c.conversation_id ?? '';
    });

    // A rider message newer than the last one we saw for that session.
    const arrived = mapped.filter((c) => {
      const last = c.last_message;
      if (!last || last.sender_type !== 'user') return false;
      const seen = lastSeenRef.current[c.id];
      return firstQueuePollDoneRef.current && (!seen || isAfter(last.created_on, seen));
    });
    mapped.forEach((c) => {
      if (c.last_message?.sender_type === 'user') {
        lastSeenRef.current[c.id] = c.last_message.created_on;
      }
    });
    firstQueuePollDoneRef.current = true;
    // Each session gets its notification (one per conversation, by tag); one chime.
    arrived.forEach((c, i) => notifyNewRiderMessage(c, { chime: i === 0 }));

    const fetchedIds = new Set(mapped.map((c) => c.id));
    setConversations((prev) => {
      const previous = new Map(prev.map((c) => [c.id, c]));
      const next: Conversation[] = [];
      mapped.forEach((c) => {
        const known = previous.get(c.id);
        if (touched(c.id)) {
          // The socket's version is newer; absent from prev means it closed.
          if (known) next.push(known);
          return;
        }
        next.push(keepDerivedContext(c, known));
      });
      // Opened over the socket while the poll was in flight.
      prev.forEach((c) => {
        if (!fetchedIds.has(c.id) && touched(c.id)) next.push(c);
      });
      return sameList(prev, next) ? prev : next;
    });

    // Gone from the queue while open on screen: closed elsewhere, and the
    // socket did not say so. Ask ant for it so the page shows how it ended.
    const active = activeConvIdRef.current;
    if (
      active &&
      !fetchedIds.has(active) &&
      !touched(active) &&
      !closedRef.current.some((c) => c.id === active) &&
      // The assistant's sessions are not in the people's queue: not gone.
      !assistantRef.current.some((c) => c.id === active)
    ) {
      adoptSession(active);
    }
  }, [adoptSession, notifyNewRiderMessage, rememberSession]);

  const pollInterval = isConnected ? SAFETY_NET_INTERVAL_MS : POLL_INTERVAL_MS;
  // With the socket down the queue poll is the only news, so it keeps
  // running in a background tab: that is what counts the hidden-tab
  // arrivals and raises the desktop notification. Browsers throttle hidden
  // timers to about one a minute anyway.
  usePolling(Boolean(adminEmail), 'queue', pollInterval, pollQueue, !isConnected);

  // ── Polling: the assistant's sessions, while the Assistant filter is on ────
  const pollAssistant = useCallback(async () => {
    const startedAt = Date.now();
    let sessions: chatApi.AntChatSession[];
    try {
      sessions = await chatApi.fetchAssistantQueue();
    } catch (error) {
      setAssistantLoaded(true);
      return;
    }
    setAssistantLoaded(true);
    const touched = (id: string) => (touchedAtRef.current[id] ?? 0) >= startedAt;
    const mapped = sessions
      .map((session) =>
        touched(session.id)
          ? sessionSnapshotsRef.current[session.id] ?? session
          : rememberSession(session)
      )
      .map(mapSession)
      .filter((c) => c.status !== 'closed' && c.handled_by === 'assistant');
    mapped.forEach((c) => {
      conversationIdsRef.current[c.id] = c.conversation_id ?? '';
    });
    const fetchedIds = new Set(mapped.map((c) => c.id));
    setAssistantConversations((prev) => {
      const previous = new Map(prev.map((c) => [c.id, c]));
      const next: Conversation[] = [];
      mapped.forEach((c) => {
        const known = previous.get(c.id);
        if (touched(c.id)) {
          if (known) next.push(known);
          return;
        }
        next.push(keepDerivedContext(c, known));
      });
      prev.forEach((c) => {
        if (!fetchedIds.has(c.id) && touched(c.id)) next.push(c);
      });
      return sameList(prev, next) ? prev : next;
    });
  }, [rememberSession]);
  usePolling(
    Boolean(adminEmail) && assistantWatched,
    'assistant',
    pollInterval,
    pollAssistant
  );
  // Read once on open whatever the filter: a rider message in a session the
  // assistant already had before the page opened must not chime.
  useEffect(() => {
    if (adminEmail) pollAssistant();
  }, [adminEmail, pollAssistant]);

  // ── Polling: the open thread ───────────────────────────────────────────────
  const pollMessages = useCallback(async () => {
    const convId = activeConvId;
    if (!convId) return;
    try {
      const conv = await resolveConversation(convId);
      const conversationId = await resolveConversationId(convId);
      const rows = await chatApi.fetchMessages(conversationId, convId);
      const mapped = applyMessages(convId, rows, conv.user_name);
      setHttpOk(true);

      // Rider messages this fetch brought in have already asked to be read
      // (applyMessages). This catches what is still unread for another
      // reason: it landed while the tab was hidden, or a read failed.
      const unread = unreadCount(conv, mapped, localReadAtRef.current[convId]);
      if (unread > 0) readOpenThreadSoon(convId);
    } catch (error) {
      setHttpOk(false);
    }
  }, [
    activeConvId,
    applyMessages,
    readOpenThreadSoon,
    resolveConversation,
    resolveConversationId,
  ]);

  usePolling(
    Boolean(adminEmail) && Boolean(activeConvId),
    activeConvId,
    pollInterval,
    pollMessages
  );

  // ── Who the riders are ─────────────────────────────────────────────────────
  // Every session on screen whose rider has not been read yet, in the queue's
  // order so the rows on screen are named first, one request per batch and
  // the names shown as each batch lands; the closed and assistant sessions
  // follow in their own chain. A rider that could not be read is asked for
  // again later, less and less often, then left alone.
  useEffect(() => {
    const lookup = riderLookupRef.current;
    const now = Date.now();
    const queued = new Set<string>();
    // How long until each rider not due yet for another ask is due.
    const dueIn: number[] = [];
    const refsOf = (list: Conversation[]): chatApi.RiderRef[] => {
      const refs: chatApi.RiderRef[] = [];
      list.forEach((c) => {
        const conversationId = c.conversation_id;
        if (!conversationId || queued.has(conversationId)) return;
        queued.add(conversationId);
        if (riders[conversationId] || lookup.pending.has(conversationId)) return;
        const failures = lookup.failures[conversationId] ?? 0;
        // Asked once, then once per wait in RIDER_RETRY_MS: after that, left alone.
        if (failures > RIDER_RETRY_MS.length) return;
        if (failures > 0) {
          const wait = RIDER_RETRY_MS[failures - 1];
          const left = wait - (now - (lookup.failedAt[conversationId] ?? 0));
          if (left > 0) {
            dueIn.push(left);
            return;
          }
        }
        refs.push({ conversationId, sessionId: c.id });
      });
      return refs;
    };
    const openRefs = refsOf([...conversations].sort(compareConversations));
    const otherRefs = refsOf([
      ...[...closedConversations].sort(compareConversations),
      ...[...assistantConversations].sort(compareConversations),
    ]);
    // The next ask is timed from here, against the same clock that decides
    // whether a rider is due: a timer that fires a hair early (browsers clamp
    // timers, Date.now rounds) or a rider due later than another's wait is
    // asked for when its own wait is up rather than skipped for the page load.
    const dueTimer =
      dueIn.length > 0
        ? setTimeout(() => setRiderRetryTick((tick) => tick + 1), Math.min(...dueIn))
        : undefined;
    const cancelDue = () => {
      if (dueTimer !== undefined) clearTimeout(dueTimer);
    };
    if (openRefs.length === 0 && otherRefs.length === 0) return cancelDue;
    [...openRefs, ...otherRefs].forEach((ref) => lookup.pending.add(ref.conversationId));

    const onBatch = ({ refs, found }: chatApi.RiderBatch) => {
      let failed = false;
      refs.forEach((ref) => {
        lookup.pending.delete(ref.conversationId);
        if (found[ref.conversationId]) {
          delete lookup.failures[ref.conversationId];
          delete lookup.failedAt[ref.conversationId];
          return;
        }
        lookup.failures[ref.conversationId] =
          (lookup.failures[ref.conversationId] ?? 0) + 1;
        lookup.failedAt[ref.conversationId] = Date.now();
        failed = true;
      });
      if (Object.keys(found).length > 0) {
        setRiders((prev) => ({ ...prev, ...found }));
      }
      // The failures are dated: the lookup runs again to time their next ask.
      if (failed) setRiderRetryTick((tick) => tick + 1);
    };
    /** One chain of batches; whatever it never answered for counts as failed. */
    const read = (refs: chatApi.RiderRef[]): Promise<void> =>
      chatApi
        .fetchRiderIdentities(refs, { onBatch })
        .then((all) => {
          const left = refs.filter((ref) => lookup.pending.has(ref.conversationId));
          const found: Record<string, chatApi.RiderIdentity> = {};
          left.forEach((ref) => {
            if (all?.[ref.conversationId])
              found[ref.conversationId] = all[ref.conversationId];
          });
          if (left.length > 0) onBatch({ refs: left, found });
        })
        .catch(() => {
          const left = refs.filter((ref) => lookup.pending.has(ref.conversationId));
          if (left.length > 0) onBatch({ refs: left, found: {} });
        });
    if (openRefs.length > 0) lookup.openChain = read(openRefs);
    if (otherRefs.length > 0) lookup.openChain.then(() => read(otherRefs));
    return cancelDue;
  }, [
    conversations,
    closedConversations,
    assistantConversations,
    riders,
    riderRetryTick,
  ]);

  /** A session with its rider's name and phone, when they have been read. */
  const withRider = useCallback(
    (c: Conversation): Conversation =>
      withRiderIdentity(c, c.conversation_id ? riders[c.conversation_id] : undefined),
    [riders]
  );
  const namedConversations = useMemo(
    () => conversations.map(withRider),
    [conversations, withRider]
  );
  const namedClosedConversations = useMemo(
    () => closedConversations.map(withRider),
    [closedConversations, withRider]
  );
  const namedAssistantConversations = useMemo(
    () => assistantConversations.map(withRider),
    [assistantConversations, withRider]
  );
  /** Rider messages signed with the rider's name once it is known. */
  const namedMessages = useMemo(() => {
    let changed = false;
    const out: Record<string, Message[]> = {};
    Object.entries(messages).forEach(([convId, thread]) => {
      const conversationId = conversationIdsRef.current[convId];
      const identity = conversationId ? riders[conversationId] : undefined;
      const name = identity ? fullName(identity) : '';
      if (!name) {
        out[convId] = thread;
        return;
      }
      let threadChanged = false;
      const next = thread.map((m) => {
        if (m.sender_type !== 'user' || m.sender_name === name) return m;
        threadChanged = true;
        return { ...m, sender_name: name };
      });
      out[convId] = threadChanged ? next : thread;
      changed = changed || threadChanged;
    });
    return changed ? out : messages;
  }, [messages, riders]);

  // ── Derived state ──────────────────────────────────────────────────────────
  const unreadByConv = useMemo(() => {
    const out: Record<string, number> = {};
    conversations.forEach((c) => {
      out[c.id] = unreadCount(c, messages[c.id], localReadAt[c.id]);
    });
    return out;
  }, [conversations, messages, localReadAt]);

  const totalUnread = Object.values(unreadByConv).reduce((a, b) => a + b, 0);

  // ── Actions ────────────────────────────────────────────────────────────────
  const joinConversation = useCallback((convId: string | null) => {
    if (activeConvIdRef.current === convId) return;
    if (pendingReadRef.current) window.clearTimeout(pendingReadRef.current.timer);
    pendingReadRef.current = null;
    activeConvIdRef.current = convId;
    liveSinceReadRef.current = {};
    setActiveConvId(convId);
  }, []);

  const loadMessages = useCallback(
    async (convId: string) => {
      const conv = await resolveConversation(convId);
      const conversationId = await resolveConversationId(convId);
      const rows = await chatApi.fetchMessages(conversationId, convId);
      const mapped = applyMessages(convId, rows, conv.user_name);
      const data: Conversation & { messages: Message[] } = { ...conv, messages: mapped };
      return data;
    },
    [applyMessages, resolveConversation, resolveConversationId]
  );

  /**
   * A reply to the rider, or an internal note when `note` is set. A reply may
   * carry a legacy photo (`image`: base64 PNG/JPEG, or a data URI) or a photo
   * or video already in the chat media bucket (`attachment`), with `content`
   * as its caption, and up to 4 buttons (`actions`); a note carries none of
   * them. Rejects with ant's error so the page can say what failed (a refused
   * attachment as chatApi.ChatAttachmentRejected); the message is on screen
   * as soon as ant has stored it.
   */
  const sendMessage = useCallback(
    async (
      convId: string,
      content: string,
      options: {
        note?: boolean;
        image?: string | null;
        attachment?: chatApi.ChatMessageAttachmentInput;
        actions?: chatApi.ChatMessageActionInput[];
      } = {}
    ): Promise<void> => {
      const key = `${convId}:${options.note ? 'note' : 'reply'}`;
      const payload = JSON.stringify({
        content,
        image: options.note ? null : options.image ?? null,
        attachment: options.note ? null : options.attachment ?? null,
        actions: options.note ? [] : options.actions ?? [],
      });
      const previous = pendingSendsRef.current.get(key);
      const pending =
        previous?.payload === payload
          ? previous
          : { payload, clientMessageId: newClientMessageId() };
      pendingSendsRef.current.set(key, pending);
      const { clientMessageId } = pending;
      const row = options.note
        ? await chatApi.addChatNote({
            sessionId: convId,
            clientMessageId,
            body: content,
          })
        : await chatApi.sendMessageAsAdmin({
            sessionId: convId,
            clientMessageId,
            body: content,
            ...(options.image ? { image: options.image } : {}),
            ...(options.attachment ? { attachment: options.attachment } : {}),
            ...(options.actions?.length ? { actions: options.actions } : {}),
          });
      if (pendingSendsRef.current.get(key) === pending)
        pendingSendsRef.current.delete(key);
      const conv = conversationsRef.current.find((c) => c.id === convId);
      const message = mapMessage(row, { sessionId: convId, userName: conv?.user_name });
      // The subscription publishes this same message too: both paths dedupe by id.
      setMessages((prev) => {
        const existing = prev[convId] || [];
        const next = foldMessage(existing, message);
        return next === existing ? prev : { ...prev, [convId]: next };
      });
      setConversations((prev) => foldMessageIntoQueue(prev, row, message));
    },
    []
  );

  /**
   * Ends the session in ant, with an optional resolution note and the tags
   * the End dialog added (ant appends them in the same write). It leaves the
   * queue for the closed list (ant also publishes it as `updated`); rejects
   * with ant's error.
   */
  const closeConversation = useCallback(
    async (
      convId: string,
      resolutionNote?: string,
      tags: string[] = []
    ): Promise<void> => {
      const session = await chatApi.closeChatSession(
        convId,
        resolutionNote?.trim() || null,
        tags
      );
      applySession(session);
    },
    [applySession]
  );

  /** Loads the most recently closed sessions for the Closed filter. */
  const loadClosed = useCallback(async (): Promise<void> => {
    setClosedLoading(true);
    try {
      const sessions = (await chatApi.fetchClosedSessions()).map(rememberSession);
      sessions.forEach((session) => {
        conversationIdsRef.current[session.id] = session.conversationId ?? '';
      });
      setClosedConversations((prev) =>
        sessions.reduce((acc, session) => foldClosedSession(acc, session), prev)
      );
    } finally {
      setClosedLoading(false);
    }
  }, [rememberSession]);

  /** Hands the session to `adminId` (ant id), or unassigns it with null. */
  const assignConversation = useCallback(
    async (convId: string, adminId: string | null) => {
      const session = await chatApi.assignChatSession(convId, adminId || null);
      applySession(session);
    },
    [applySession]
  );

  /**
   * Takes a session from the assistant: ant assigns it to this agent and a
   * person handles it from now on, so it moves to the queue. On a session a
   * person already handles (another agent's) it is "assign to me". Rejects
   * with ant's error.
   */
  const takeOver = useCallback(
    async (convId: string) => {
      const session = await chatApi.takeOverChatSession(convId);
      applySession(session);
    },
    [applySession]
  );

  const setPriority = useCallback(
    async (convId: string, priority: 'normal' | 'urgent') => {
      const session = await chatApi.setChatSessionPriority(convId, priority);
      applySession(session);
    },
    [applySession]
  );

  /**
   * One glossary tag on, one off: ant edits the stored array in SQL, so two
   * agents' clicks compose and nothing here sends an array read a minute ago.
   * Closed sessions take tags too, so the session ant returns is folded into
   * whichever list holds it (queue, assistant, closed). Rejects with ant's
   * error ("not in the glossary or retired: ...").
   */
  const addTag = useCallback(
    async (convId: string, tag: string) => {
      applySession(await chatApi.addChatSessionTag(convId, tag));
    },
    [applySession]
  );

  const removeTag = useCallback(
    async (convId: string, tag: string) => {
      applySession(await chatApi.removeChatSessionTag(convId, tag));
    },
    [applySession]
  );

  return {
    myAdminId,
    queueLoaded,
    isConnected,
    transport,
    conversations: namedConversations,
    closedConversations: namedClosedConversations,
    closedLoading,
    loadClosed,
    assistantConversations: namedAssistantConversations,
    assistantLoaded,
    watchAssistant: setAssistantWatched,
    takeOver,
    messages: namedMessages,
    withRider,
    unreadByConv,
    totalUnread,
    arrivedWhileHidden,
    joinConversation,
    loadMessages,
    openSession,
    refreshSession,
    sendMessage,
    closeConversation,
    assignConversation,
    markAsRead,
    addTag,
    removeTag,
    setPriority,
  };
}
